/**
 * ==============================================================================
 * GULCAN CART DRAWER JAVASCRIPT
 * ==============================================================================
 * Handles all cart drawer interactions:
 * - Open/close drawer
 * - AJAX quantity updates
 * - AJAX item removal
 * - Free shipping bar updates
 * - Upsell product loading
 * - Auto-open on add-to-cart
 * ==============================================================================
 */

var GULCAN_CART_DRAWER_BUILD = '2026-05-19-v3-csm-fallback';
(function() {
  'use strict';

  // ==============================================================================
  // CONFIGURATION
  // ==============================================================================

  var SELECTORS = {
    drawer: '#gulcan-cart-drawer',
    overlay: '#gulcan-cart-overlay',
    panel: '#gulcan-cart-panel',
    close: '#gulcan-cart-close',
    content: '[data-cart-drawer-content]',
    items: '[data-cart-drawer-items]',
    item: '[data-cart-item]',
    footer: '[data-cart-drawer-footer]',
    empty: '[data-cart-drawer-empty]',

    // Count elements
    count: '[data-cart-drawer-count]',
    total: '[data-cart-drawer-total]',
    savings: '[data-cart-drawer-savings]',

    // Quantity controls - use gulcan prefix to avoid Impulse theme interference
    qtyInput: '[data-gulcan-qty]',
    qtyDecrease: '[data-gulcan-decrease]',
    qtyIncrease: '[data-gulcan-increase]',
    linePrice: '[data-line-price]',

    // Shipping bar
    shippingBar: '[data-shipping-bar]',
    shippingText: '[data-shipping-text]',
    shippingRemaining: '[data-shipping-remaining]',
    shippingSuccess: '[data-shipping-success]',
    shippingProgress: '[data-progress]',

    // Upsell
    upsellTrack: '[data-upsell-track]',
    upsellPrev: '[data-upsell-prev]',
    upsellNext: '[data-upsell-next]',

    // Order note
    noteToggle: '[data-cart-note-toggle]',
    noteField: '[data-cart-note-field]',
    noteInput: '[data-cart-note-input]',
    noteSaved: '[data-cart-note-saved]',

    // Continue shopping
    continueBtn: '#gulcan-cart-continue'
  };

  // ==============================================================================
  // STATE
  // ==============================================================================

  var state = {
    isOpen: false,
    isUpdating: false,
    freeShippingThreshold: 5000, // Default €50 in cents
    scrollPosition: 0
  };

  var elements = {};

  // ==============================================================================
  // INITIALIZATION
  // ==============================================================================

  function init() {
    // Cache DOM elements
    elements.drawer = document.querySelector(SELECTORS.drawer);
    if (!elements.drawer) return;

    elements.overlay = document.querySelector(SELECTORS.overlay);
    elements.panel = document.querySelector(SELECTORS.panel);
    elements.close = document.querySelector(SELECTORS.close);
    elements.content = elements.drawer.querySelector(SELECTORS.content);
    elements.upsellTrack = elements.drawer.querySelector(SELECTORS.upsellTrack);

    // Get free shipping threshold
    var threshold = elements.drawer.dataset.freeShippingThreshold;
    if (threshold) {
      state.freeShippingThreshold = parseInt(threshold, 10) * 100;
    }

    // Bind events
    bindEvents();

    // Load upsell products
    loadUpsellProducts();

    // Build the shipping bar on first load. The bar is hidden by default
    // in the Liquid (so we don't flash wrong info), so this is what makes
    // it appear once we know the visitor's country and current cart total.
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function(r) { return r.json(); })
      .then(function(cart) { updateShippingBar(cart); })
      .catch(function() { /* leave bar hidden on failure */ });

    // Check if we should auto-open (e.g., coming from /cart URL)
    checkAutoOpen();
  }

  // ==============================================================================
  // EVENT BINDING
  // ==============================================================================

  function bindEvents() {
    // Close button
    if (elements.close) {
      elements.close.addEventListener('click', closeDrawer);
    }

    // Overlay click
    if (elements.overlay) {
      elements.overlay.addEventListener('click', closeDrawer);
    }

    // Continue shopping button (in empty state)
    var continueBtn = elements.drawer.querySelector(SELECTORS.continueBtn);
    if (continueBtn) {
      continueBtn.addEventListener('click', function(e) {
        e.preventDefault();
        closeDrawer();
        window.location.href = this.href;
      });
    }

    // Quantity controls (delegated) - minus at qty 1 = delete
    elements.drawer.addEventListener('click', handleQuantityClick);
    elements.drawer.addEventListener('change', handleQuantityChange);

    // Upsell navigation
    var upsellPrev = elements.drawer.querySelector(SELECTORS.upsellPrev);
    var upsellNext = elements.drawer.querySelector(SELECTORS.upsellNext);

    if (upsellPrev) {
      upsellPrev.addEventListener('click', function() {
        scrollUpsell(-1);
      });
    }

    if (upsellNext) {
      upsellNext.addEventListener('click', function() {
        scrollUpsell(1);
      });
    }

    // Empty products navigation (when cart is empty - same carousel style)
    bindEmptyProductsNav();

    // Recently viewed products navigation
    bindRecentlyViewedNav();

    // Previously bought products navigation
    bindPreviouslyBoughtNav();

    // Add-to-cart buttons in upsell/empty products (delegated)
    bindUpsellAddButtons();

    // Load recently viewed products
    loadRecentlyViewedProducts();

    // Escape key to close
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && state.isOpen) {
        closeDrawer();
      }
    });

    // Listen for add-to-cart events (from product pages)
    document.addEventListener('cart:add', handleCartAdd);
    document.addEventListener('cart:updated', handleCartUpdated);

    // Listen for Impulse theme's quick add event
    document.addEventListener('ajaxProduct:added', handleImpulseQuickAdd);

    // Intercept cart icon clicks (will be set up by header)
    // window.GulcanCartDrawer.open() is the API

    // Intercept /cart navigation
    interceptCartLinks();

    // Quick add buttons on product cards (delegated event on document)
    // Use capture phase to run before Impulse theme's handler
    bindQuickAddButtons();

    // Listen for country detection event to update shipping display
    document.addEventListener('gulcan:countryDetected', function() {
      // Refresh shipping bar with detected country
      fetch('/cart.js')
        .then(function(response) { return response.json(); })
        .then(function(cart) { updateShippingBar(cart); });
    });

    // Order note toggle + auto-save
    bindCartNote();
  }

  // ==============================================================================
  // DRAWER OPEN/CLOSE
  // ==============================================================================

  function openDrawer() {
    if (state.isOpen) return;

    state.isOpen = true;
    state.scrollPosition = window.scrollY;

    elements.drawer.classList.add('is-open');
    elements.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('gulcan-cart-open');

    // Focus on close button
    if (elements.close) {
      setTimeout(function() {
        elements.close.focus();
      }, 100);
    }

    // Refresh cart data
    refreshCartContent();
  }

  function closeDrawer() {
    if (!state.isOpen) return;

    state.isOpen = false;

    elements.drawer.classList.remove('is-open');
    elements.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('gulcan-cart-open');

    // Restore scroll position
    window.scrollTo(0, state.scrollPosition);
  }

  function toggleDrawer() {
    if (state.isOpen) {
      closeDrawer();
    } else {
      openDrawer();
    }
  }

  // ==============================================================================
  // QUANTITY HANDLERS
  // ==============================================================================

  function handleQuantityClick(e) {
    var decreaseBtn = e.target.closest(SELECTORS.qtyDecrease);
    var increaseBtn = e.target.closest(SELECTORS.qtyIncrease);

    if (!decreaseBtn && !increaseBtn) return;
    if (state.isUpdating) return;

    var item = e.target.closest(SELECTORS.item);
    if (!item) return;

    var input = item.querySelector(SELECTORS.qtyInput);
    if (!input) return;

    var quantity = parseInt(input.value, 10);

    if (decreaseBtn) {
      // If quantity is 1, delete the item (set to 0)
      if (quantity <= 1) {
        item.classList.add('is-removing');
        setTimeout(function() {
          updateCartItem(item, 0);
        }, 300);
        return;
      }
      quantity = quantity - 1;
    } else if (increaseBtn) {
      quantity = Math.min(99, quantity + 1);
    }

    input.value = quantity;
    updateCartItem(item, quantity);
  }

  function handleQuantityChange(e) {
    if (!e.target.matches(SELECTORS.qtyInput)) return;
    if (state.isUpdating) return;

    var item = e.target.closest(SELECTORS.item);
    if (!item) return;

    var quantity = parseInt(e.target.value, 10);

    if (isNaN(quantity) || quantity < 0) {
      quantity = 0;
    } else if (quantity > 99) {
      quantity = 99;
    }

    // If quantity is 0, delete the item
    if (quantity === 0) {
      item.classList.add('is-removing');
      setTimeout(function() {
        updateCartItem(item, 0);
      }, 300);
      return;
    }

    e.target.value = quantity;
    updateCartItem(item, quantity);
  }

  // ==============================================================================
  // CART UPDATE FUNCTIONS
  // ==============================================================================

  function updateCartItem(item, quantity) {
    var lineKey = item.dataset.lineKey;
    if (!lineKey) return;

    setLoading(true);
    item.classList.add('is-updating');

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    })
    .then(function(response) {
      if (!response.ok) {
        return response.json().then(function(err) {
          return Promise.reject(err);
        });
      }
      return response.json();
    })
    .then(function(cart) {
      updateDrawerUI(cart);
      item.classList.remove('is-updating');

      // Dispatch event for header count update
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    })
    .catch(function(error) {
      console.error('Cart update error:', error);
      item.classList.remove('is-updating');
      setLoading(false);

      // Refresh to sync state
      refreshCartContent();
    });
  }

  function refreshCartContent() {
    fetch('/cart.js')
      .then(function(response) {
        return response.json();
      })
      .then(function(cart) {
        updateDrawerUI(cart);
      })
      .catch(function(error) {
        console.error('Cart fetch error:', error);
      });
  }

  // ==============================================================================
  // UI UPDATE FUNCTIONS
  // ==============================================================================

  function updateDrawerUI(cart) {
    // Update count in drawer
    var countEl = elements.drawer.querySelector(SELECTORS.count);
    if (countEl) {
      countEl.textContent = cart.item_count;
    }

    // Update all cart counts (header, etc.)
    updateAllCartCounts(cart.item_count);

    // Update total
    var totalEl = elements.drawer.querySelector(SELECTORS.total);
    if (totalEl) {
      totalEl.textContent = formatMoney(cart.total_price);
    }

    // Update savings (if applicable)
    var savingsEl = elements.drawer.querySelector(SELECTORS.savings);
    if (savingsEl) {
      if (cart.original_total_price > cart.total_price) {
        savingsEl.textContent = formatMoney(cart.original_total_price);
        savingsEl.style.display = '';
      } else {
        savingsEl.style.display = 'none';
      }
    }

    // Update shipping bar
    updateShippingBar(cart);

    // Update unit prices (not line totals - we show unit price per item)
    cart.items.forEach(function(item) {
      var itemEl = elements.drawer.querySelector('[data-line-key="' + item.key + '"]');
      if (itemEl) {
        var priceEl = itemEl.querySelector(SELECTORS.linePrice);
        if (priceEl) {
          priceEl.textContent = formatMoney(item.final_price);
        }

        var qtyInput = itemEl.querySelector(SELECTORS.qtyInput);
        if (qtyInput) {
          qtyInput.value = item.quantity;
        }
        // Note: decrease button is never disabled - clicking at qty 1 deletes item
      }
    });

    // Check for empty cart - reload drawer content if needed
    if (cart.item_count === 0) {
      reloadDrawerContent();
    } else {
      // Check if any cart items have keys not present in the DOM
      // This happens with bundle apps (Kaching) where Shopify changes keys after cart updates
      var hasOrphanedItems = cart.items.some(function(item) {
        return !elements.drawer.querySelector('[data-line-key="' + item.key + '"]');
      });

      if (hasOrphanedItems) {
        // Keys changed — skip DOM removal and reload fresh HTML to avoid flash
        reloadDrawerContent();
      } else {
        // Keys stable — remove items that are no longer in cart
        var itemElements = elements.drawer.querySelectorAll(SELECTORS.item);
        itemElements.forEach(function(itemEl) {
          var key = itemEl.dataset.lineKey;
          var stillInCart = cart.items.some(function(item) {
            return item.key === key;
          });

          if (!stillInCart) {
            itemEl.remove();
          }
        });

      }
    }

    // Update bundle progress messages
    updateBundleProgress(cart);

    setLoading(false);
  }

  // ==============================================================================
  // KACHING BUNDLE PROGRESS
  // ==============================================================================
  // Tracks discount % per deal. After qty increase, if % didn't improve → max reached.
  // Tier data is cached in sessionStorage so it persists across page loads.

  var bundleTierCache = {};

  function loadBundleTierCache() {
    try {
      var stored = sessionStorage.getItem('gulcan_bundle_tiers');
      if (stored) bundleTierCache = JSON.parse(stored);
    } catch (e) { /* ignore */ }
  }

  function saveBundleTierCache() {
    try {
      sessionStorage.setItem('gulcan_bundle_tiers', JSON.stringify(bundleTierCache));
    } catch (e) { /* ignore */ }
  }

  loadBundleTierCache();

  function updateBundleProgress(cart) {
    cart.items.forEach(function(item) {
      // Check for __kaching_bundles property (accessible in JS, not in Liquid)
      var kachingProp = item.properties && item.properties['__kaching_bundles'];
      if (!kachingProp) return;

      // Extract deal ID
      var dealMatch = kachingProp.match(/"deal":"([^"]+)"/);
      if (!dealMatch) return;
      var dealId = dealMatch[1];

      // Get current discount %
      var currentPct = 0;
      if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
        currentPct = parseFloat(item.line_level_discount_allocations[0].discount_application.value) || 0;
      }

      // Initialize tier cache for this deal
      if (!bundleTierCache[dealId]) {
        bundleTierCache[dealId] = { tiers: {}, maxQty: 0 };
      }

      var cache = bundleTierCache[dealId];

      // Record this tier
      if (currentPct > 0) {
        cache.tiers[item.quantity] = currentPct;
      }

      // Detect max: discount didn't improve vs previous tier
      if (item.quantity > 1 && cache.tiers[item.quantity - 1] !== undefined) {
        if (currentPct <= cache.tiers[item.quantity - 1] && currentPct > 0) {
          cache.maxQty = item.quantity - 1;
        }
      }

      // Discount disappeared — previous qty was max
      if (item.quantity > 1 && currentPct === 0 && cache.tiers[item.quantity - 1] !== undefined) {
        cache.maxQty = item.quantity - 1;
      }

      saveBundleTierCache();

      // Find the DOM element for this item
      var itemEl = elements.drawer.querySelector('[data-line-key="' + item.key + '"]');
      if (!itemEl) return;

      // Inject "Bundel" tag if not already present
      var titleRow = itemEl.querySelector('.gulcan-cart-drawer__item-title-row');
      if (titleRow && !titleRow.querySelector('.gulcan-cart-drawer__bundle-tag')) {
        var tag = document.createElement('span');
        tag.className = 'gulcan-cart-drawer__bundle-tag';
        tag.textContent = 'Bundel';
        titleRow.appendChild(tag);
      }

      // Inject or update progress text
      var detailsEl = itemEl.querySelector('.gulcan-cart-drawer__item-details');
      if (!detailsEl) return;

      var progressEl = itemEl.querySelector('.gulcan-cart-drawer__bundle-progress');
      if (!progressEl) {
        progressEl = document.createElement('p');
        progressEl.className = 'gulcan-cart-drawer__bundle-progress';
        progressEl.setAttribute('data-bundle-progress', '');
        detailsEl.appendChild(progressEl);
      }

      // Determine message based on tier data
      var nextQty = item.quantity + 1;
      var nextPct = cache.tiers[nextQty];

      if (cache.maxQty > 0 && item.quantity >= cache.maxQty) {
        // At max — no more tiers
        progressEl.textContent = '';
      } else if (nextPct !== undefined) {
        // We know the next tier's %
        progressEl.textContent = 'Voeg er nog 1 toe voor ' + nextPct + '% korting';
      } else if (currentPct === 0 && item.quantity === 1) {
        // No discount yet at qty 1
        progressEl.textContent = 'Voeg er nog 1 toe voor bundelkorting';
      } else {
        // Don't know next tier — probe in background to find out
        progressEl.textContent = '';
        probeNextTier(item.key, item.quantity, currentPct, dealId, progressEl);
      }
    });
  }

  // Probes qty+1 to discover the next tier's discount, then reverts.
  // Runs silently in the background — no UI updates during probe.
  var probeInProgress = {};

  function probeNextTier(lineKey, currentQty, currentPct, dealId, progressEl) {
    // Only probe once per deal+qty combo
    var probeKey = dealId + '_' + currentQty;
    if (probeInProgress[probeKey]) return;
    probeInProgress[probeKey] = true;

    // Step 1: bump qty to currentQty + 1
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: currentQty + 1 })
    })
    .then(function(r) { return r.json(); })
    .then(function(probeCart) {
      // Read the discount at qty+1
      var probeItem = probeCart.items.find(function(i) {
        return i.properties && i.properties['__kaching_bundles'] &&
               i.properties['__kaching_bundles'].indexOf('"deal":"' + dealId + '"') !== -1;
      });

      var probePct = 0;
      if (probeItem && probeItem.line_level_discount_allocations && probeItem.line_level_discount_allocations.length > 0) {
        probePct = parseFloat(probeItem.line_level_discount_allocations[0].discount_application.value) || 0;
      }

      // Step 2: revert back to original qty (use the new key from probe response)
      var revertKey = probeItem ? probeItem.key : lineKey;
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: revertKey, quantity: currentQty })
      })
      .then(function() { return probePct; });
    })
    .then(function(probePct) {
      var cache = bundleTierCache[dealId];

      if (probePct > currentPct) {
        // Next tier exists — record it and show message
        cache.tiers[currentQty + 1] = probePct;
        progressEl.textContent = 'Voeg er nog 1 toe voor ' + probePct + '% korting';
      } else {
        // No improvement — current qty is max
        cache.maxQty = currentQty;
        progressEl.textContent = '';
      }

      saveBundleTierCache();
      probeInProgress[probeKey] = false;

      // Reload drawer to sync keys after probe
      reloadDrawerContent();
    })
    .catch(function() {
      probeInProgress[probeKey] = false;
    });
  }

  // Country names in Dutch for shipping display
  var COUNTRY_NAMES = {
    NL: 'Nederland', BE: 'België', DE: 'Duitsland', FR: 'Frankrijk',
    AT: 'Oostenrijk', LU: 'Luxemburg', IT: 'Italië', ES: 'Spanje',
    PT: 'Portugal', GB: 'Verenigd Koninkrijk', CH: 'Zwitserland',
    DK: 'Denemarken', SE: 'Zweden'
  };

  /**
   * Per-country free shipping thresholds. MUST match the Shipping zones in
   * Shopify admin (Settings → Shipping and delivery), otherwise customers
   * see one threshold in the cart drawer and another at checkout.
   *
   * Value is the cart subtotal in cents needed to unlock free shipping.
   * Countries NOT listed here (or listed with no threshold) get the entire
   * shipping bar hidden — we'd rather show nothing than misleading info.
   */
  var SHIPPING_CONFIG = {
    NL: { freeShippingThreshold: 10000 }, // €100
    BE: { freeShippingThreshold: 20000 }  // €200
    // DE, AT, FR, DK, SE: no free shipping — bar stays hidden for these.
  };

  // Track if we've attempted country detection
  var countryDetectionAttempted = false;

  /**
   * Detect user's country via IP geolocation API.
   * Re-verifies on every page load (cache is only for instant initial
   * paint via the inline script in the snippet — we don't let a stale
   * NL cache stick if the visitor is now coming from a different IP).
   */
  function detectCountryIfNeeded(callback) {
    // Already fetched this page load — reuse that result
    if (countryDetectionAttempted) {
      if (callback) callback(window.gulcanDetectedCountry || 'NL');
      return;
    }

    countryDetectionAttempted = true;

    // Persist successful detection to localStorage so the cart-drawer
    // Liquid can pre-apply the right visibility on the next visit. We
    // ONLY cache real detections — never the NL fallback after a failure,
    // otherwise a single bad ipapi.co response would lock a BE customer
    // into the €100 NL threshold forever.
    function cacheCountry(code) {
      window.gulcanDetectedCountry = code;
      try { localStorage.setItem('gulcanCountry', code); } catch (e) { /* ignore */ }
    }

    // Use the in-memory default but skip persistence to avoid poisoning
    // the cache when our IP services are down or rate-limiting us.
    function fallbackToNL() {
      window.gulcanDetectedCountry = 'NL';
      if (callback) callback('NL');
    }

    // Chain of IP geolocation services. ipapi.co rate-limits aggressively
    // (returns 429 with no CORS header), so we lead with services that
    // are more generous and CORS-friendly. Order = ranked by reliability
    // observed in the wild.
    var providers = [
      {
        name: 'api.country.is',
        url: 'https://api.country.is/',
        parse: function(res) { return res.json().then(function(j) { return (j && j.country || '').toUpperCase(); }); }
      },
      {
        name: 'bigdatacloud',
        url: 'https://api-bdc.net/data/country-by-ip',
        parse: function(res) { return res.json().then(function(j) { return (j && j.countryCode || '').toUpperCase(); }); }
      },
      {
        name: 'ipwhois.io',
        url: 'https://ipwhois.app/json/',
        parse: function(res) { return res.json().then(function(j) { return (j && j.country_code || '').toUpperCase(); }); }
      },
      {
        name: 'freeipapi',
        url: 'https://freeipapi.com/api/json',
        parse: function(res) { return res.json().then(function(j) { return (j && j.countryCode || '').toUpperCase(); }); }
      },
      {
        name: 'ipapi.co',
        url: 'https://ipapi.co/country_code/',
        parse: function(res) { return res.text().then(function(t) { return t.trim().toUpperCase(); }); }
      }
    ];

    function tryProvider(index) {
      if (index >= providers.length) {
        // Last resort: try to piggyback on csmADACountryDetection,
        // which other apps on the site populate with the visitor's
        // real country. Some networks block every IP geo API we know
        // (corporate firewalls, pi-holes, etc.), so this is a useful
        // safety net.
        try {
          var csm = localStorage.getItem('csmADACountryDetection');
          if (csm) {
            var parsed = JSON.parse(csm);
            var c = (parsed && parsed.country || '').toUpperCase();
            if (c.length === 2) {
              console.log('[Gulcan country detect] csmADACountryDetection (fallback) → ' + c);
              cacheCountry(c);
              if (callback) callback(c);
              return;
            }
          }
        } catch (e) { /* malformed JSON or blocked storage */ }

        console.warn('[Gulcan country detect] All providers failed, falling back to NL');
        fallbackToNL();
        return;
      }
      var p = providers[index];
      fetch(p.url, { method: 'GET' })
        .then(function(response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return p.parse(response);
        })
        .then(function(code) {
          if (code && code.length === 2) {
            console.log('[Gulcan country detect] ' + p.name + ' → ' + code);
            cacheCountry(code);
            if (callback) callback(code);
          } else {
            console.warn('[Gulcan country detect] ' + p.name + ' returned invalid code: ' + JSON.stringify(code));
            tryProvider(index + 1);
          }
        })
        .catch(function(err) {
          console.warn('[Gulcan country detect] ' + p.name + ' failed:', err.message || err);
          tryProvider(index + 1);
        });
    }

    tryProvider(0);
  }

  function updateShippingBar(cart) {
    // Detect country if not already known, then update shipping bar
    detectCountryIfNeeded(function(countryCode) {
      updateShippingBarWithCountry(cart, countryCode);
    });
  }

  function updateShippingBarWithCountry(cart, countryCode) {
    var shippingBar = elements.drawer.querySelector(SELECTORS.shippingBar);
    if (!shippingBar) return;

    var config = SHIPPING_CONFIG[countryCode];

    // Hide the entire bar in two cases:
    //   1. Unknown country (no config entry) — we have no reliable info
    //   2. Country has no free shipping threshold — nothing to count down to
    // Better to show nothing than misleading info or a useless "cost" line.
    if (!config || config.freeShippingThreshold <= 0) {
      shippingBar.style.display = 'none';
      return;
    }

    shippingBar.style.display = 'block';

    var threshold = config.freeShippingThreshold;
    var remaining = threshold - cart.total_price;
    var progressPercent = Math.min(100, (cart.total_price / threshold) * 100);

    var progressEl  = elements.drawer.querySelector(SELECTORS.shippingProgress);
    var textEl      = elements.drawer.querySelector(SELECTORS.shippingText);
    var successEl   = elements.drawer.querySelector(SELECTORS.shippingSuccess);
    var remainingEl = elements.drawer.querySelector(SELECTORS.shippingRemaining);

    if (progressEl) progressEl.style.width = progressPercent + '%';

    if (remaining <= 0) {
      if (textEl)    textEl.style.display    = 'none';
      if (successEl) successEl.style.display = 'flex';
    } else {
      if (textEl)    textEl.style.display    = 'block';
      if (successEl) successEl.style.display = 'none';
      if (remainingEl) remainingEl.textContent = formatMoney(remaining);
    }
  }

  // ==============================================================================
  // ORDER NOTE
  // ==============================================================================

  var noteDebounceTimer = null;

  function bindCartNote() {
    var toggle = elements.drawer.querySelector(SELECTORS.noteToggle);
    var field = elements.drawer.querySelector(SELECTORS.noteField);
    var input = elements.drawer.querySelector(SELECTORS.noteInput);

    if (!toggle || !field) return;

    toggle.addEventListener('click', function() {
      var expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      field.classList.toggle('is-open', !expanded);

      if (!expanded && input) {
        // Focus textarea when opening
        setTimeout(function() { input.focus(); }, 260);
      } else {
        // Clear pending save timer on close, save immediately if there's a pending change
        if (noteDebounceTimer) {
          clearTimeout(noteDebounceTimer);
          noteDebounceTimer = null;
          if (input) saveCartNote(input.value);
        }
      }
    });

    if (input) {
      input.addEventListener('input', function() {
        clearTimeout(noteDebounceTimer);
        noteDebounceTimer = setTimeout(function() {
          saveCartNote(input.value);
        }, 500);
      });
    }
  }

  function saveCartNote(note) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note })
    })
    .then(function(response) {
      if (!response.ok) throw new Error('Failed to save note');
      return response.json();
    })
    .then(function() {
      // Show "Opgeslagen" feedback briefly
      var savedEl = elements.drawer.querySelector(SELECTORS.noteSaved);
      if (savedEl) {
        savedEl.classList.add('is-visible');
        setTimeout(function() {
          savedEl.classList.remove('is-visible');
        }, 1500);
      }
    })
    .catch(function(error) {
      console.error('Cart note save error:', error);
    });
  }

  function reloadDrawerContent() {
    // Clear any pending note save before replacing DOM
    clearTimeout(noteDebounceTimer);
    noteDebounceTimer = null;

    // Fetch fresh drawer HTML via section rendering API
    fetch('/?section_id=gulcan-cart-drawer')
      .then(function(response) {
        return response.text();
      })
      .then(function(html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        // Replace entire panel content to handle footer appearing/disappearing
        var newPanel = doc.querySelector(SELECTORS.panel);
        if (newPanel && elements.panel) {
          elements.panel.innerHTML = newPanel.innerHTML;

          // innerHTML doesn't execute the inline <script> baked into the
          // shipping bar in the snippet, so apply the cached-country
          // visibility ourselves immediately — before the async cart
          // fetch below — to avoid a bar-flash for non-NL/BE visitors.
          var cachedCountry = window.gulcanDetectedCountry;
          if (!cachedCountry) {
            try { cachedCountry = localStorage.getItem('gulcanCountry'); } catch (e) { /* ignore */ }
          }
          if (cachedCountry && cachedCountry !== 'NL' && cachedCountry !== 'BE') {
            var shippingBarEl = elements.drawer.querySelector(SELECTORS.shippingBar);
            if (shippingBarEl) shippingBarEl.style.display = 'none';
          }

          // Re-cache elements after replacing panel content
          elements.content = elements.drawer.querySelector(SELECTORS.content);
          elements.upsellTrack = elements.drawer.querySelector(SELECTORS.upsellTrack);
          elements.close = elements.drawer.querySelector(SELECTORS.close);

          // Rebind close button
          if (elements.close) {
            elements.close.addEventListener('click', closeDrawer);
          }

          // Rebind upsell navigation
          var upsellPrev = elements.drawer.querySelector(SELECTORS.upsellPrev);
          var upsellNext = elements.drawer.querySelector(SELECTORS.upsellNext);
          if (upsellPrev) {
            upsellPrev.addEventListener('click', function() { scrollUpsell(-1); });
          }
          if (upsellNext) {
            upsellNext.addEventListener('click', function() { scrollUpsell(1); });
          }

          // Rebind empty products navigation (for empty cart state)
          var emptyPrev = elements.drawer.querySelector('[data-empty-prev]');
          var emptyNext = elements.drawer.querySelector('[data-empty-next]');
          if (emptyPrev) {
            emptyPrev.addEventListener('click', function() { scrollEmptyProducts(-1); });
          }
          if (emptyNext) {
            emptyNext.addEventListener('click', function() { scrollEmptyProducts(1); });
          }

          // Rebind recently viewed navigation
          var recentPrev = elements.drawer.querySelector('[data-recent-prev]');
          var recentNext = elements.drawer.querySelector('[data-recent-next]');
          if (recentPrev) {
            recentPrev.addEventListener('click', function() { scrollRecentlyViewed(-1); });
          }
          if (recentNext) {
            recentNext.addEventListener('click', function() { scrollRecentlyViewed(1); });
          }

          // Rebind previously bought navigation
          var boughtPrev = elements.drawer.querySelector('[data-bought-prev]');
          var boughtNext = elements.drawer.querySelector('[data-bought-next]');
          if (boughtPrev) {
            boughtPrev.addEventListener('click', function() { scrollPreviouslyBought(-1); });
          }
          if (boughtNext) {
            boughtNext.addEventListener('click', function() { scrollPreviouslyBought(1); });
          }

          // Rebind order note toggle
          bindCartNote();

          // Reload upsell products
          loadUpsellProducts();

          // Reload recently viewed products
          loadRecentlyViewedProducts();

          // Re-apply country-based shipping and bundle progress after content reload
          fetch('/cart.js')
            .then(function(response) { return response.json(); })
            .then(function(cart) {
              updateShippingBar(cart);
              updateBundleProgress(cart);
            });
        }
      })
      .catch(function(error) {
        console.error('Drawer reload error:', error);
      });
  }

  // ==============================================================================
  // ADD TO CART HANDLER
  // ==============================================================================

  function handleCartAdd(e) {
    // Auto-open drawer when item is added
    openDrawer();
    // Reload full drawer content to show new items
    reloadDrawerContent();
    // Update all cart counts
    fetch('/cart.js')
      .then(function(response) { return response.json(); })
      .then(function(cart) { updateAllCartCounts(cart.item_count); });
  }

  function handleCartUpdated(e) {
    if (e.detail) {
      updateDrawerUI(e.detail);
    }
  }

  /**
   * Handle Impulse theme's ajaxProduct:added event.
   * This fires after quick add via Impulse's quick-add-cart snippet.
   */
  function handleImpulseQuickAdd(e) {
    // Open drawer
    openDrawer();

    // Reload drawer content to show new item
    reloadDrawerContent();

    // Fetch cart to update counts
    fetch('/cart.js')
      .then(function(response) {
        return response.json();
      })
      .then(function(cart) {
        updateAllCartCounts(cart.item_count);
      });
  }

  // ==============================================================================
  // UPSELL PRODUCTS
  // ==============================================================================

  /**
   * Load upsell products with smart price filtering for better conversion.
   *
   * Price logic:
   * - Low carts (< €40): show products up to €15 (same price range)
   * - Normal carts: max 25% of cart total, capped at €30
   * - Fallback: if recommendations don't have cheap items, fetch from all products
   *
   * This encourages impulse buys instead of showing expensive items.
   */
  function loadUpsellProducts() {
    if (!elements.upsellTrack) return;

    // Get upsell section to hide if no products pass filter
    var upsellSection = elements.drawer.querySelector('[data-cart-drawer-upsell]');

    fetch('/cart.js')
      .then(function(response) {
        return response.json();
      })
      .then(function(cart) {
        if (cart.items.length === 0) return;

        // Store cart total for price filtering (in cents)
        var cartTotal = cart.total_price;
        var productId = cart.items[0].product_id;

        // Get product IDs already in cart to exclude from upsells
        var cartProductIds = cart.items.map(function(item) {
          return item.product_id;
        });

        // ==============================================================
        // SMART PRICE FILTERING
        // ==============================================================
        // Threshold: €40 (4000 cents) - below this use fixed low price range
        var MIN_CART_THRESHOLD = 4000;
        // Low cart max price: €15 (1500 cents)
        var LOW_CART_MAX_PRICE = 1500;

        var maxPrice;
        if (cartTotal < MIN_CART_THRESHOLD) {
          // Low cart value: show products in similar low price range (€0-€15)
          maxPrice = LOW_CART_MAX_PRICE;
        } else {
          // Normal cart: 25% of cart total, capped at €30 (3000 cents)
          maxPrice = Math.min(cartTotal * 0.25, 3000);
        }

        // First try recommendations API
        return fetch('/recommendations/products.json?product_id=' + productId + '&limit=12')
          .then(function(response) {
            if (!response) return { products: [] };
            return response.json();
          })
          .then(function(data) {
            var products = data.products || [];

            // Filter by price and exclude items already in cart
            var filteredProducts = products.filter(function(product) {
              return product.price <= maxPrice && cartProductIds.indexOf(product.id) === -1;
            });

            // If recommendations have cheap items, use them
            if (filteredProducts.length >= 3) {
              if (upsellSection) upsellSection.style.display = '';
              renderUpsellProducts(filteredProducts.slice(0, 6));
              return;
            }

            // FALLBACK: Fetch from all products sorted by price (cheapest first)
            // This ensures we always have impulse-buy items to show
            return fetchCheapProducts(maxPrice, cartProductIds)
              .then(function(cheapProducts) {
                // Combine any cheap recommendations with cheap products from catalog
                var combined = filteredProducts.concat(cheapProducts);

                // Remove duplicates by product ID
                var seen = {};
                combined = combined.filter(function(product) {
                  if (seen[product.id]) return false;
                  seen[product.id] = true;
                  return true;
                });

                if (combined.length === 0) {
                  if (upsellSection) upsellSection.style.display = 'none';
                  return;
                }

                if (upsellSection) upsellSection.style.display = '';
                renderUpsellProducts(combined.slice(0, 6));
              });
          });
      })
      .catch(function(error) {
        console.warn('Upsell load error:', error);
      });
  }

  /**
   * Fetch cheap products from the catalog as fallback for upsells.
   * Uses Shopify's collection API to get products sorted by price.
   */
  function fetchCheapProducts(maxPrice, excludeIds) {
    // Fetch from 'all' collection - Shopify sorts by best-selling by default
    // We'll filter by price client-side
    return fetch('/collections/all/products.json?limit=50')
      .then(function(response) {
        return response.json();
      })
      .then(function(data) {
        var products = data.products || [];

        // Filter by price and exclude cart items
        // Note: products.json returns price as string, need to convert to cents
        var filtered = products.filter(function(product) {
          // Get first variant price (in string format like "14.95")
          var priceStr = product.variants[0].price;
          var priceCents = Math.round(parseFloat(priceStr) * 100);

          return priceCents <= maxPrice && excludeIds.indexOf(product.id) === -1;
        });

        // Shuffle the filtered products for price variety
        // This ensures we show a mix of prices (€3, €15, €25, etc.) not just the cheapest
        filtered = shuffleArray(filtered);

        // Transform to match recommendations API format (price in cents)
        return filtered.slice(0, 12).map(function(product) {
          var variant = product.variants[0];
          return {
            id: product.id,
            title: product.title,
            handle: product.handle,
            url: '/products/' + product.handle,
            price: Math.round(parseFloat(variant.price) * 100),
            compare_at_price: variant.compare_at_price ? Math.round(parseFloat(variant.compare_at_price) * 100) : null,
            featured_image: product.images[0] ? product.images[0].src : '',
            images: product.images.map(function(img) { return img.src; }),
            variants: product.variants.map(function(v) {
              return {
                id: v.id,
                price: Math.round(parseFloat(v.price) * 100),
                compare_at_price: v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : null
              };
            })
          };
        });
      })
      .catch(function(error) {
        console.warn('Cheap products fetch error:', error);
        return [];
      });
  }

  function renderUpsellProducts(products) {
    if (!elements.upsellTrack) return;

    var html = products.map(function(product) {
      var image = product.featured_image || product.images[0];
      var variantId = product.variants[0].id;
      // Compute price range across variants so we can show "vanaf" when sizes
      // / variants have different prices. Variant prices arrive as cents here.
      var variantPrices = (product.variants || []).map(function(v) { return v.price; });
      var priceMin = variantPrices.length ? Math.min.apply(null, variantPrices) : product.price;
      var priceMax = variantPrices.length ? Math.max.apply(null, variantPrices) : product.price;
      var priceVaries = priceMax > priceMin;
      var compareAtPrice = product.compare_at_price;
      var fromPrefix = priceVaries ? '<span class="gulcan-price-from">vanaf </span>' : '';

      // Build price HTML with sale price support
      var priceHtml = '<span class="gulcan-cart-drawer__upsell-price-current">' + fromPrefix + formatMoney(priceMin) + '</span>';
      if (compareAtPrice && compareAtPrice > priceMin) {
        priceHtml += '<span class="gulcan-cart-drawer__upsell-price-compare">' + formatMoney(compareAtPrice) + '</span>';
      }

      return '<div class="gulcan-cart-drawer__upsell-item">' +
        '<a href="' + product.url + '" class="gulcan-cart-drawer__upsell-image">' +
          '<img src="' + getSizedImageUrl(image, '120x120') + '" alt="' + escapeHtml(product.title) + '" width="56" height="56" loading="lazy">' +
        '</a>' +
        '<div class="gulcan-cart-drawer__upsell-info">' +
          '<a href="' + product.url + '" class="gulcan-cart-drawer__upsell-name">' + escapeHtml(product.title) + '</a>' +
          '<p class="gulcan-cart-drawer__upsell-price">' + priceHtml + '</p>' +
        '</div>' +
        '<button type="button" class="gulcan-cart-drawer__upsell-add" data-add-to-cart="' + variantId + '"><svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor"><path d="M292.31-115.38q-25.31 0-42.66-17.35-17.34-17.35-17.34-42.65 0-25.31 17.34-42.66 17.35-17.34 42.66-17.34 25.31 0 42.65 17.34 17.35 17.35 17.35 42.66 0 25.3-17.35 42.65-17.34 17.35-42.65 17.35Zm375.38 0q-25.31 0-42.65-17.35-17.35-17.35-17.35-42.65 0-25.31 17.35-42.66 17.34-17.34 42.65-17.34t42.66 17.34q17.34 17.35 17.34 42.66 0 25.3-17.34 42.65-17.35 17.35-42.66 17.35ZM235.23-740 342-515.38h265.38q6.93 0 12.31-3.47 5.39-3.46 9.23-9.61l104.62-190q4.61-8.46.77-15-3.85-6.54-13.08-6.54h-486Zm-19.54-40h520.77q26.08 0 39.23 21.27 13.16 21.27 1.39 43.81l-114.31 208.3q-8.69 14.62-22.58 22.93-13.88 8.31-30.5 8.31H324l-48.62 89.23q-6.15 9.23-.38 20 5.77 10.77 17.31 10.77h435.38v40H292.31q-35 0-52.23-29.5-17.23-29.5-.85-59.27l60.15-107.23L152.31-820H80v-40h97.69l38 80ZM342-515.38h280-280Z"/></svg></button>' +
      '</div>';
    }).join('');

    elements.upsellTrack.innerHTML = html;

    // Add-to-cart handled by delegated listener in bindUpsellAddButtons()
  }

  function scrollUpsell(direction) {
    if (!elements.upsellTrack) return;

    var scrollAmount = 172; // Item width + gap
    elements.upsellTrack.scrollBy({
      left: scrollAmount * direction,
      behavior: 'smooth'
    });
  }

  /**
   * Bind navigation for empty products carousel (when cart is empty).
   * Uses same scroll behavior as upsell carousel.
   */
  function bindEmptyProductsNav() {
    var emptyPrev = elements.drawer.querySelector('[data-empty-prev]');
    var emptyNext = elements.drawer.querySelector('[data-empty-next]');

    if (emptyPrev) {
      emptyPrev.addEventListener('click', function() {
        scrollEmptyProducts(-1);
      });
    }

    if (emptyNext) {
      emptyNext.addEventListener('click', function() {
        scrollEmptyProducts(1);
      });
    }
  }

  function scrollEmptyProducts(direction) {
    var track = elements.drawer.querySelector('[data-empty-track]');
    if (!track) return;

    // Full width of item (100% + gap)
    var scrollAmount = track.offsetWidth;
    track.scrollBy({
      left: scrollAmount * direction,
      behavior: 'smooth'
    });
  }

  /**
   * Bind navigation for recently viewed products carousel.
   */
  function bindRecentlyViewedNav() {
    var recentPrev = elements.drawer.querySelector('[data-recent-prev]');
    var recentNext = elements.drawer.querySelector('[data-recent-next]');

    if (recentPrev) {
      recentPrev.addEventListener('click', function() {
        scrollRecentlyViewed(-1);
      });
    }

    if (recentNext) {
      recentNext.addEventListener('click', function() {
        scrollRecentlyViewed(1);
      });
    }
  }

  function scrollRecentlyViewed(direction) {
    var track = elements.drawer.querySelector('[data-recently-viewed-track]');
    if (!track) return;

    var scrollAmount = track.offsetWidth;
    track.scrollBy({
      left: scrollAmount * direction,
      behavior: 'smooth'
    });
  }

  /**
   * Bind navigation for previously bought products carousel.
   */
  function bindPreviouslyBoughtNav() {
    var boughtPrev = elements.drawer.querySelector('[data-bought-prev]');
    var boughtNext = elements.drawer.querySelector('[data-bought-next]');

    if (boughtPrev) {
      boughtPrev.addEventListener('click', function() {
        scrollPreviouslyBought(-1);
      });
    }

    if (boughtNext) {
      boughtNext.addEventListener('click', function() {
        scrollPreviouslyBought(1);
      });
    }
  }

  function scrollPreviouslyBought(direction) {
    var track = elements.drawer.querySelector('[data-bought-track]');
    if (!track) return;

    var scrollAmount = track.offsetWidth;
    track.scrollBy({
      left: scrollAmount * direction,
      behavior: 'smooth'
    });
  }

  /**
   * Load recently viewed products from localStorage and render in carousel.
   * Recently viewed products are stored by the theme/PDP page.
   */
  function loadRecentlyViewedProducts() {
    var section = elements.drawer.querySelector('[data-recently-viewed-section]');
    var track = elements.drawer.querySelector('[data-recently-viewed-track]');
    if (!section || !track) return;

    // Try to get recently viewed from localStorage (common Shopify pattern)
    var recentlyViewed = [];

    // Try different localStorage keys used by themes
    // Our custom key (gulcan_recently_viewed) is checked first
    var storageKeys = [
      'gulcan_recently_viewed',
      'recently-viewed',
      'recentlyViewed',
      'recently_viewed_products',
      'Shopify.theme.recentlyViewed',
      'theme:recently-viewed'
    ];

    for (var i = 0; i < storageKeys.length; i++) {
      var data = localStorage.getItem(storageKeys[i]);
      if (data) {
        try {
          var parsed = JSON.parse(data);
          if (Array.isArray(parsed) && parsed.length > 0) {
            recentlyViewed = parsed;
            break;
          }
        } catch (e) {
          // Try comma-separated handles
          if (typeof data === 'string' && data.includes(',')) {
            recentlyViewed = data.split(',').filter(Boolean);
            break;
          }
        }
      }
    }

    // If no recently viewed, hide section
    if (recentlyViewed.length === 0) {
      section.style.display = 'none';
      return;
    }

    // Fetch product data and render
    var handles = recentlyViewed.slice(0, 6);
    var fetchPromises = handles.map(function(item) {
      // Item could be handle string or object with handle property
      var handle = typeof item === 'string' ? item : (item.handle || item.url || '');
      if (!handle) return Promise.resolve(null);

      // Clean handle (remove /products/ prefix if present)
      handle = handle.replace('/products/', '').replace(/\//g, '');

      return fetch('/products/' + handle + '.json')
        .then(function(res) { return res.json(); })
        .then(function(data) { return data.product; })
        .catch(function() { return null; });
    });

    Promise.all(fetchPromises).then(function(products) {
      var validProducts = products.filter(function(p) { return p && p.available !== false; });

      if (validProducts.length === 0) {
        section.style.display = 'none';
        return;
      }

      var html = validProducts.map(function(product) {
        // Get image URL - images array contains objects with src property
        var image = '';
        if (product.images && product.images.length > 0) {
          image = product.images[0].src || product.images[0];
        }
        var variantId = product.variants[0].id;
        // Compute price range across variants. product.json returns prices as
        // decimal strings ("12.95") so parseFloat them all and Min/Max.
        var variantPrices = (product.variants || []).map(function(v) {
          return parseFloat(v.price);
        }).filter(function(n) { return !isNaN(n); });
        var priceMin = variantPrices.length ? Math.min.apply(null, variantPrices) : 0;
        var priceMax = variantPrices.length ? Math.max.apply(null, variantPrices) : 0;
        var priceVaries = priceMax > priceMin;
        var compareAtPrice = product.variants[0].compare_at_price;
        var priceFormatted = '€' + priceMin.toFixed(2).replace('.', ',');
        var fromPrefix = priceVaries ? '<span class="gulcan-price-from">vanaf </span>' : '';

        // Build price HTML with sale price support
        var priceHtml = '<span class="gulcan-cart-drawer__upsell-price-current">' + fromPrefix + priceFormatted + '</span>';
        if (compareAtPrice && parseFloat(compareAtPrice) > priceMin) {
          var compareFormatted = '€' + parseFloat(compareAtPrice).toFixed(2).replace('.', ',');
          priceHtml += '<span class="gulcan-cart-drawer__upsell-price-compare">' + compareFormatted + '</span>';
        }

        return '<div class="gulcan-cart-drawer__upsell-item">' +
          '<a href="/products/' + product.handle + '" class="gulcan-cart-drawer__upsell-image">' +
            '<img src="' + getSizedImageUrl(image, '120x120') + '" alt="' + escapeHtml(product.title) + '" width="56" height="56" loading="lazy">' +
          '</a>' +
          '<div class="gulcan-cart-drawer__upsell-info">' +
            '<a href="/products/' + product.handle + '" class="gulcan-cart-drawer__upsell-name">' + escapeHtml(product.title) + '</a>' +
            '<p class="gulcan-cart-drawer__upsell-price">' + priceHtml + '</p>' +
          '</div>' +
          '<button type="button" class="gulcan-cart-drawer__upsell-add" data-add-to-cart="' + variantId + '"><svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor"><path d="M292.31-115.38q-25.31 0-42.66-17.35-17.34-17.35-17.34-42.65 0-25.31 17.34-42.66 17.35-17.34 42.66-17.34 25.31 0 42.65 17.34 17.35 17.35 17.35 42.66 0 25.3-17.35 42.65-17.34 17.35-42.65 17.35Zm375.38 0q-25.31 0-42.65-17.35-17.35-17.35-17.35-42.65 0-25.31 17.35-42.66 17.34-17.34 42.65-17.34t42.66 17.34q17.34 17.35 17.34 42.66 0 25.3-17.34 42.65-17.35 17.35-42.66 17.35ZM235.23-740 342-515.38h265.38q6.93 0 12.31-3.47 5.39-3.46 9.23-9.61l104.62-190q4.61-8.46.77-15-3.85-6.54-13.08-6.54h-486Zm-19.54-40h520.77q26.08 0 39.23 21.27 13.16 21.27 1.39 43.81l-114.31 208.3q-8.69 14.62-22.58 22.93-13.88 8.31-30.5 8.31H324l-48.62 89.23q-6.15 9.23-.38 20 5.77 10.77 17.31 10.77h435.38v40H292.31q-35 0-52.23-29.5-17.23-29.5-.85-59.27l60.15-107.23L152.31-820H80v-40h97.69l38 80ZM342-515.38h280-280Z"/></svg></button>' +
        '</div>';
      }).join('');

      track.innerHTML = html;
      section.style.display = '';
    });
  }

  /**
   * Bind add-to-cart buttons in upsell and empty products sections.
   * Uses event delegation to handle dynamically loaded content.
   */
  function bindUpsellAddButtons() {
    elements.drawer.addEventListener('click', function(e) {
      var addBtn = e.target.closest('.gulcan-cart-drawer__upsell-add[data-add-to-cart]');
      if (!addBtn) return;

      var variantId = addBtn.dataset.addToCart;
      if (!variantId) return;

      // Prevent double clicks
      if (addBtn.disabled) return;
      var cartIconSvg = addBtn.innerHTML;
      addBtn.disabled = true;
      addBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 0 20 20" width="20px" fill="currentColor" class="icon--spinner"><circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="50" stroke-linecap="round"/></svg>';

      addToCart(variantId, 1);

      // Reset button after a short delay (reloadDrawerContent will update UI)
      setTimeout(function() {
        addBtn.disabled = false;
        addBtn.innerHTML = cartIconSvg;
      }, 1500);
    });
  }

  function addToCart(variantId, quantity) {
    setLoading(true);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    })
    .then(function(response) {
      return response.json();
    })
    .then(function() {
      // Refresh cart content
      return fetch('/cart.js');
    })
    .then(function(response) {
      return response.json();
    })
    .then(function(cart) {
      // Reload drawer to show new item
      reloadDrawerContent();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      setLoading(false);
    })
    .catch(function(error) {
      console.error('Add to cart error:', error);
      setLoading(false);
    });
  }

  // ==============================================================================
  // INTERCEPT CART LINKS
  // ==============================================================================

  function interceptCartLinks() {
    document.addEventListener('click', function(e) {
      var link = e.target.closest('a[href="/cart"]');
      if (link && !link.dataset.noIntercept) {
        e.preventDefault();
        openDrawer();
      }
    });
  }

  function checkAutoOpen() {
    // If current URL is /cart, open drawer and redirect to homepage
    if (window.location.pathname === '/cart') {
      // Redirect to homepage with drawer open
      window.history.replaceState({}, '', '/');
      openDrawer();
    }
  }

  // ==============================================================================
  // QUICK ADD BUTTONS (Product Cards)
  // ==============================================================================

  /**
   * Bind click events on quick add buttons found on product cards.
   * Uses event delegation on document for dynamically loaded cards.
   * Expects buttons with class .gulcan-atc-btn or .gulcan-atc-btn--impulse
   * and data-gulcan-variant attribute.
   * Note: We use these classes (not .gulcan-quick-add-cart) and data-gulcan-variant
   *       to avoid Impulse theme's custom handler intercepting our clicks.
   */
  function bindQuickAddButtons() {
    document.addEventListener('click', function(e) {
      // Support both Gulcan cards (.gulcan-atc-btn) and Impulse cards (.gulcan-atc-btn--impulse)
      var quickAddBtn = e.target.closest('.gulcan-atc-btn, .gulcan-atc-btn--impulse');
      if (!quickAddBtn) return;

      e.preventDefault();
      e.stopPropagation(); // Prevent card link click

      // Check if this is a "notify me" button (sold out product)
      // Supports both Gulcan (.gulcan-notify-btn) and Impulse (.gulcan-notify-btn--impulse) styles
      if (quickAddBtn.classList.contains('gulcan-notify-btn') || quickAddBtn.classList.contains('gulcan-notify-btn--impulse')) {
        // Navigate to product page with hash to trigger back-in-stock notification
        var productUrl = quickAddBtn.dataset.productUrl;
        if (productUrl) {
          window.location.href = productUrl + '#notify-me';
        }
        return;
      }

      // Check if already adding (using 'loading' class to match CSS)
      if (quickAddBtn.classList.contains('loading')) return;

      // Use data-gulcan-variant to avoid Impulse theme interference
      var variantId = quickAddBtn.dataset.gulcanVariant;
      if (!variantId) {
        console.warn('GulcanCartDrawer: Quick add button missing data-gulcan-variant');
        return;
      }

      // Add loading state to button (matches gulcan-products.css)
      quickAddBtn.classList.add('loading');

      // For Impulse buttons, also show the loading spinner element
      var loadingEl = quickAddBtn.querySelector('.quick-add-btn__loading');
      var textEl = quickAddBtn.querySelector('.quick-add-btn__text');
      if (loadingEl) loadingEl.style.display = '';
      if (textEl) textEl.style.display = 'none';

      // Add to cart, open drawer, refresh
      quickAddToCart(variantId, 1, quickAddBtn);
    });
  }

  /**
   * Add product to cart via quick add button.
   * Opens drawer and updates UI immediately.
   */
  function quickAddToCart(variantId, quantity, button) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    })
    .then(function(response) {
      if (!response.ok) {
        return response.json().then(function(err) {
          return Promise.reject(err);
        });
      }
      return response.json();
    })
    .then(function(item) {
      // Open drawer immediately
      openDrawer();

      // Fetch full cart for UI update
      return fetch('/cart.js');
    })
    .then(function(response) {
      return response.json();
    })
    .then(function(cart) {
      // Reload drawer content to show new item
      reloadDrawerContent();

      // Update all cart count elements (header, etc.)
      updateAllCartCounts(cart.item_count);

      // Dispatch event for header count + other listeners
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

      // Update button state (using 'loading' and 'added' to match gulcan-products.css)
      if (button) {
        button.classList.remove('loading');
        button.classList.add('added');

        // For Impulse buttons, hide loading and text (CSS handles checkmark via ::before)
        var loadingEl = button.querySelector('.quick-add-btn__loading');
        var textEl = button.querySelector('.quick-add-btn__text');
        if (loadingEl) loadingEl.style.display = 'none';
        if (textEl) textEl.style.display = 'none';
        // Note: Don't show .quick-add-btn__added - CSS ::before handles the checkmark

        // Reset added state after 2s
        setTimeout(function() {
          button.classList.remove('added');
          // Reset Impulse button elements
          if (textEl) textEl.style.display = '';
        }, 2000);
      }
    })
    .catch(function(error) {
      console.error('GulcanCartDrawer: Quick add failed', error);

      if (button) {
        button.classList.remove('loading');

        // For Impulse buttons, reset inner elements
        var loadingEl = button.querySelector('.quick-add-btn__loading');
        var textEl = button.querySelector('.quick-add-btn__text');
        if (loadingEl) loadingEl.style.display = 'none';
        if (textEl) textEl.style.display = '';
      }

      // Show error to user
      if (error.description) {
        alert(error.description);
      }
    });
  }

  // ==============================================================================
  // UTILITY FUNCTIONS
  // ==============================================================================

  function setLoading(loading) {
    state.isUpdating = loading;
    if (elements.drawer) {
      elements.drawer.classList.toggle('is-loading', loading);
    }
  }

  function formatMoney(cents) {
    var euros = (cents / 100).toFixed(2);
    return '€' + euros.replace('.', ',');
  }

  function escapeHtml(text) {
    var div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function getSizedImageUrl(url, size) {
    if (!url) return '';

    // Handle Shopify CDN URLs
    if (url.indexOf('cdn.shopify.com') !== -1) {
      return url.replace(/(_\d+x\d+)?(\.[^.]+)$/, '_' + size + '$2');
    }

    return url;
  }

  /**
   * Shuffle array randomly (Fisher-Yates algorithm).
   * Used to randomize upsell products for price variety.
   */
  function shuffleArray(array) {
    var shuffled = array.slice(); // Copy array
    for (var i = shuffled.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var temp = shuffled[i];
      shuffled[i] = shuffled[j];
      shuffled[j] = temp;
    }
    return shuffled;
  }

  /**
   * Update cart count on all known cart count elements.
   * Supports: Gulcan header, Impulse theme, and common selectors.
   */
  function updateAllCartCounts(count) {
    // Common cart count selectors across themes
    var selectors = [
      '#gulcan-cart-count',           // Gulcan header
      '[data-cart-count]',            // Generic data attribute
      '.cart-link__count',            // Impulse theme
      '.cart-count',                  // Common class
      '.header__cart-count',          // Common header class
      '.site-header__cart-count',     // Another common pattern
      '[data-cart-drawer-count]'      // Our drawer count
    ];

    selectors.forEach(function(selector) {
      var elements = document.querySelectorAll(selector);
      elements.forEach(function(el) {
        el.textContent = count;
        // Toggle visibility based on count
        if (count > 0) {
          el.style.display = '';
          el.classList.add('has-items');
        } else {
          el.classList.remove('has-items');
        }
      });
    });

    // Also update the drawer's own count
    var drawerCount = document.querySelector('[data-cart-drawer-count]');
    if (drawerCount) {
      drawerCount.textContent = count;
    }
  }

  // ==============================================================================
  // INITIALIZATION
  // ==============================================================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ==============================================================================
  // PUBLIC API
  // ==============================================================================

  window.GulcanCartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    toggle: toggleDrawer,
    refresh: refreshCartContent
  };

})();
